import { createFileRoute, useRouter } from '@tanstack/react-router'
import type { JSX } from 'react'

import { Link } from '../design-system/link.tsx'
import { Panel } from '../design-system/panel.tsx'
import { INTERNAL_PAGES } from '../lib/pages.ts'

export const Route = createFileRoute('/')({
  component: HomePage
})

/**
 * PageList — every page in the webapp.
 *
 * The same declared list the rail reads, so the two cannot disagree. There is
 * no request and no loading state: per-page access control was removed, so
 * nothing has to be asked before this can be drawn.
 *
 * @returns The list.
 */
function PageList(): JSX.Element {
  const router = useRouter()

  return (
    <ul data-testid="home-pages" className="mt-6 flex flex-col gap-3">
      {INTERNAL_PAGES.map(page => (
        <li
          key={page.id}
          className="border-border rounded-lg border p-4 shadow-xs"
        >
          <Link
            data-testid={`home-page-${page.id}`}
            // A declared path is a string at runtime — see the note in
            // __root.tsx for why this is not a TanStack Link.
            href={page.path}
            onNavigate={() => {
              router.history.push(page.path)
            }}
            className="text-base font-medium hover:underline"
          >
            {page.title}
          </Link>
          <p className="text-muted-foreground font-mono text-xs">{page.path}</p>
        </li>
      ))}
    </ul>
  )
}

/**
 * HomePage — what an operator lands on.
 *
 * @returns The home surface.
 */
function HomePage(): JSX.Element {
  return (
    <Panel
      data-testid="home-panel"
      title="ui-internal"
      description="The single shared internal webapp — every BlytzPay internal app lives inside it."
    >
      <p data-testid="home-body" className="text-muted-foreground text-sm">
        {'Pick an app from the rail.'}
      </p>
      <PageList />
    </Panel>
  )
}
